import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import { FLUITY_LAST_PATH_KEY, isAppPath } from "@/lib/persistRoute";
import { ThemeProvider } from "@/components/ThemeProvider";
import { AuthProvider } from "@/hooks/useAuth";
import { OrganizationProvider } from "@/contexts/OrganizationContext";
import { KbAgentChatProvider } from "@/contexts/KbAgentChatContext";
import { Layout } from "@/components/Layout";
import { ModuleGuard } from "@/components/ModuleGuard";
import { OnboardingGuard } from "@/components/OnboardingGuard";
import { ProductSelectionGuard } from "@/components/ProductSelectionGuard";
import { ProductBasedRoute } from "@/components/ProductBasedRoute";

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import VerifyEmail from "./pages/VerifyEmail";
import PendingOrgAccess from "./pages/PendingOrgAccess";
import Chats from "./pages/Chats";
import Performance from "./pages/Performance";
import Profile from "./pages/Profile";
import NotFound from "./pages/NotFound";

const queryCache = new QueryCache({
  onError: () => {},
  onSuccess: () => {},
});

const queryClient = new QueryClient({
  queryCache,
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
      staleTime: 60_000,
    },
  },
});

function PersistLastRoute() {
  const location = useLocation();
  useEffect(() => {
    if (isAppPath(location.pathname)) {
      const fullPath = location.pathname + (location.search || "") + (location.hash || "");
      localStorage.setItem(FLUITY_LAST_PATH_KEY, fullPath);
    }
  }, [location.pathname, location.search, location.hash]);
  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider defaultTheme="light" storageKey="fluity-ui-theme">
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <PersistLastRoute />
          <AuthProvider>
            <OrganizationProvider>
              <KbAgentChatProvider>
              <Sentry.ErrorBoundary fallback={({ error, resetError }) => (
                <div className="min-h-screen flex items-center justify-center bg-background p-4">
                  <div className="max-w-md w-full rounded-lg border p-6 space-y-4">
                    <div className="flex items-center gap-2 text-destructive font-semibold">
                      Algo deu errado
                    </div>
                    <p className="text-sm text-muted-foreground">Ocorreu um erro inesperado. Por favor, recarregue a página.</p>
                    <button onClick={() => window.location.reload()} className="w-full h-9 rounded-md bg-primary text-primary-foreground text-sm font-medium">
                      Recarregar Página
                    </button>
                  </div>
                </div>
              )}>
                <SentryRoutes>
                  <Route path="/" element={<Navigate to="/login" replace />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/accept-invite" element={<AuthCallback />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route path="/pending-access" element={<PendingOrgAccess />} />

                  <Route element={<OnboardingGuard><ProductSelectionGuard /></OnboardingGuard>}>
                    <Route
                      path="/chat"
                      element={
                        <ProductBasedRoute>
                          <Layout>
                            <ModuleGuard>
                              <Chats />
                            </ModuleGuard>
                          </Layout>
                        </ProductBasedRoute>
                      }
                    />
                    <Route
                      path="/profile"
                      element={
                        <ProductBasedRoute>
                          <Layout>
                            <Profile />
                          </Layout>
                        </ProductBasedRoute>
                      }
                    />

                    <Route
                      path="/chat/performance"
                      element={
                        <ProductBasedRoute>
                          <Layout>
                            <Performance />
                          </Layout>
                        </ProductBasedRoute>
                      }
                    />

                    {/* Redirects de compatibilidade */}
                    <Route path="/chat/chats" element={<Navigate to="/chat" replace />} />
                    <Route path="/dashboard" element={<Navigate to="/chat" replace />} />
                    <Route path="/chats" element={<Navigate to="/chat" replace />} />
                    <Route path="/chats/chats" element={<Navigate to="/chat" replace />} />
                    <Route path="/starter/chats" element={<Navigate to="/chat" replace />} />
                    <Route path="/crm/chats" element={<Navigate to="/chat" replace />} />
                    <Route path="/settings" element={<Navigate to="/profile" replace />} />
                    <Route path="/signup" element={<Navigate to="/login" replace />} />
                    <Route path="/onboarding" element={<Navigate to="/chat" replace />} />
                  </Route>

                  <Route path="*" element={<NotFound />} />
                </SentryRoutes>
              </Sentry.ErrorBoundary>
              </KbAgentChatProvider>
            </OrganizationProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
